import React, { useEffect, useState } from 'react';
import { Info, ChevronRight } from 'lucide-react';
import gafferIcon from '../../assets/gaffer-head-icon.png';
import * as banterApi from '../../api/banter';
import { formatDayHeading } from '../../lib/formatDate';

/**
 * What the house has been saying, on the page you land on.
 *
 * The board itself is platform-wide and read-only for players; this is the last few
 * posts from it, below everything that is waiting on you. **Global news is never more
 * urgent than your own deadline** — that ordering is the whole argument for putting it
 * here rather than at the top.
 *
 * Quiet like its neighbours: an empty board renders nothing at all, and a failed read is
 * swallowed rather than leading a landing page with an error about a sidebar. Both
 * matter more here than anywhere else on this page, because on the day this ships the
 * board *is* empty, and a permanent "From the house" panel with nothing under it is how
 * people learn to skip the space it occupies.
 *
 * `See all` appears only once there is more here than the three shown. A link to the
 * same three posts is worse than no link.
 */
const SHOWN = 3;

function HousePanel({ onOpenBoard }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let live = true;
    banterApi.getHouseBoard()
      .then((rows) => {
        if (live) setPosts(rows || []);
      })
      .catch(() => {
        if (live) setPosts([]);
      });
    return () => {
      live = false;
    };
  }, []);

  if (posts.length === 0) return null;

  const shown = posts.slice(0, SHOWN);

  return (
    <section className="house-panel">
      <div className="house-panel-head">
        <h2 className="section-eyebrow">From the house</h2>
        {posts.length > SHOWN && onOpenBoard && (
          <button type="button" className="house-see-all" onClick={onOpenBoard}>
            See all
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="house-posts">
        {shown.map((post) => <HouseCard key={post.id} post={post} />)}
      </div>
    </section>
  );
}

/**
 * One post, in one of two voices.
 *
 * `STRAIGHT` and `NOTICE` carry no line of the Gaffer's — the server refuses to attach
 * one — and both are drawn quieter for it. **An announcement delivered in a comedy voice
 * is an announcement people scroll past**, which is the whole reason `PostRegister`
 * exists.
 *
 * Only `NOTICE` loses him entirely, and the register's own definition is what decides
 * that: `STRAIGHT` is "still him, no jokes", where `NOTICE` is "out of character
 * entirely… read by lawyers and regulators". The same rule as `BanterPost`, which is
 * what draws this board on its own page — the two surfaces must not disagree about what
 * a post is.
 */
function HouseCard({ post }) {
  const serious = post.register === 'STRAIGHT' || post.register === 'NOTICE';
  const outOfCharacter = post.register === 'NOTICE';
  const facts = parseFacts(post.payload);
  const badge = labelFor(post.postType);

  return (
    <article className={`house-card${serious ? ' is-serious' : ''}`}>
      <div className="house-card-head">
        {outOfCharacter ? (
          <span className="house-card-icon" aria-hidden="true"><Info size={18} /></span>
        ) : (
          <img className="house-card-face" src={gafferIcon} alt="" aria-hidden="true" />
        )}
        <span className="house-card-who">{outOfCharacter ? 'Notice' : 'The Gaffer'}</span>
        {badge && <span className="house-card-badge">{badge}</span>}
        <span className="house-card-when">{formatDayHeading(post.createdAt)}</span>
      </div>

      {facts && (
        <dl className="house-card-facts">
          {Object.entries(facts).map(([key, value]) => (
            <div key={key}>
              <dt>{key}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {post.body && <p className="house-card-body">{post.body}</p>}
    </article>
  );
}

// A post whose payload will not parse still has to render — the facts card is dropped
// rather than taking the panel down with it.
function parseFacts(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

// Plain banter carries no badge. Everything else does, because the badge is how you tell
// "he is joking" from "this changes your deadline" without reading first.
function labelFor(postType) {
  switch (postType) {
    case 'RESCHEDULE': return 'Reschedule';
    case 'RESULT_NOTE': return 'Result';
    case 'ANNOUNCEMENT': return 'Announcement';
    default: return null;
  }
}

export default HousePanel;
